import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FiTrash2 } from "react-icons/fi";
import { TbHistory, TbHistoryOff  } from "react-icons/tb";
import { RiExpandDiagonalFill, RiCollapseDiagonalFill } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import config from "../../config/config.json";
import "./ai-chat.css";

export default function AiChat({ isAIChatOpen, toggleAIChat }) {
  const [messages, setMessages] = useState([
    { role: "ai", text: "Hi 👋 I'm AI assistant. Ask me anything." }
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [sendHistory, setSendHistory] = useState(true);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const clearChat = () => {
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!input.trim() || typing) return;
    const messageText = input;
    const userMsg = {
      role: "user",
      text: messageText
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setTyping(true);
    try {
      const res = await fetch(`${config.aiApiURL}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: messageText,
          history: sendHistory
            ? messages.map(m => ({
                role: m.role,
                content: m.text
              }))
            : []
        })
      });
      const data = await res.json();
      const aiMsg = {
        role: "ai",
        text: data.reply
      };
      setMessages(prev => [...prev, aiMsg]);
    }
    catch {
      setMessages(prev => [
        ...prev,
        {
          role: "ai",
          text: "❌ Unable to connect to AI server."
        }
      ]);
    }
    finally {
      setTyping(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter")
      sendMessage();
  };

  return (
    <>
      <div
        className="ai-float-btn"
        onClick={toggleAIChat}
      >
        {isAIChatOpen ? "✕" : "🤖"}
      </div>
      <div className={`ai-chat-container ${isAIChatOpen ? "open" : ""} ${maximized ? "maximized" : ""}`}>
        <div className="ai-chat-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
            <div className="ai-avatar">🤖</div>
            <div>
              <div className="ai-title">AI Assistant</div>
              <div className="ai-status">
                {typing ? "Typing..." : "Online"}
              </div>
            </div>
          </div>
          <div
            className="ai-history-toggle"
            onClick={() => setSendHistory(prev => !prev)}
            title="Send chat history"
          >
            {sendHistory ? <TbHistory size={18}/> : <TbHistoryOff size={18}/>}
          </div>
          <div
            className="ai-clear-btn"
            onClick={clearChat}
            title="Clear chat"
          >
           {/* 🧹 */}
           <FiTrash2 size={18}/>
          </div>
          <div
            className="ai-max-btn"
            onClick={() => setMaximized(prev => !prev)}
          >
            {maximized ? <RiCollapseDiagonalFill size={18} /> : <RiExpandDiagonalFill size={18}/>}
          </div>
          <div
            className="ai-close-btn"
            onClick={toggleAIChat}
            title="Close AI Assistant"
          >
            <IoClose size={18} />
          </div>
        </div>
        <div className="ai-chat-body">
          {messages.map((msg, i) => (
            <div key={i} className={`msg-row ${msg.role}`}>
              <div className="msg-bubble">
                <ReactMarkdown
                  components={{
                    code({ inline, className, children }) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline ? (
                        <SyntaxHighlighter
                          style={oneDark}
                          language={match?.[1] || "javascript"}
                          PreTag="div"
                        >
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      ) : (
                        <code>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {msg.text}
                </ReactMarkdown>
              </div>
            </div>
          ))}
          {typing && (
            <div className="msg-row ai">
              <div className="msg-bubble typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="ai-chat-footer">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Type your message..."
            disabled={typing}
          />
          <button
            onClick={sendMessage}
            disabled={typing}
          >
            ➤
          </button>
        </div>
      </div>
    </>
  );
}